<template>
  <div
    class="flex self-center border-[1.5px] border-solid rounded-md px-4 py-5 md:p-6 min-h-[96px] h-full shadow-md bg-white"
  >
    <div class="flex flex-col w-full">
      <div v-if="hasIcon" class="self-start mb-2">
        <Image :media="icon" class="min-w-4 max-w-14" />
      </div>
      <div v-if="title?.value" class="text-input-label uppercase">
        <RichText :field="title" class="group-active:text-racq-navy group-active:underline" />
      </div>
      <div v-if="subTitle?.value" class="text-input-label pt-2">
        <RichText :field="subTitle" class="group-active:text-racq-navy group-active:underline" />
      </div>
      <slot v-if="includeBankContent" name="bankContent">
        <div class="flex mt-2">
          <div class="flex flex-col pr-3 w-fit max-w-1/2">
            <div class="flex items-end">
              <span
                v-if="leftMainLabel?.value || $slots.leftMainLabel"
                class="whitespace-normal break-words text-xl3-bold"
              >
                <slot name="leftMainLabel">
                  <RichText :field="leftMainLabel" />
                </slot>
              </span>
              <span v-if="leftMainLabelSuffix?.value"
                ><RichText :field="leftMainLabelSuffix" class="text-base-bold"
              /></span>
            </div>
            <div v-if="leftSubLabel?.value"><RichText :field="leftSubLabel" class="text-m-bold" /></div>
          </div>
          <div class="flex flex-col pl-3 w-fit max-w-1/2 border-l-2 border-racq-white">
            <div class="flex items-end">
              <span
                v-if="rightMainLabel?.value || $slots.rightMainLabel"
                class="whitespace-normal break-words text-xl3-bold"
              >
                <slot name="rightMainLabel">
                  <RichText :field="rightMainLabel" />
                </slot>
              </span>
              <span v-if="rightMainLabelSuffix?.value"
                ><RichText :field="rightMainLabelSuffix" class="text-base-bold"
              /></span>
            </div>
            <div v-if="rightSubLabel?.value"><RichText :field="rightSubLabel" class="text-m-bold" /></div>
          </div>
        </div>
        <div v-if="footerText?.value">
          <RichTextWithModal :field="footerText" :modalContentHtml="openModalContent" class="text-xs mt-3 mb-2" />
        </div>
      </slot>
      <div v-if="description?.value" class="text-block text-m-brand mr-6">
        <RichText :field="description" class="group-active:text-racq-med-grey group-active:no-underline" />
      </div>
      <div class="flex pt-3 gap-4 mt-auto flex-wrap">
        <Link v-if="leftButtonLabel?.value" :field="leftButtonLink" class="self-center min-w-0">
          <div
            :class="`btn btn-md md:btn-sm ${leftButtonClass} text-left whitespace-normal break-words !h-auto !leading-[1rem]`"
            :aria-label="leftButtonLabel?.value"
          >
            {{ leftButtonLabel?.value }}
          </div>
        </Link>
        <Link v-if="rightButtonLabel?.value" :field="rightButtonLink" class="self-center min-w-0">
          <div
            :class="`btn btn-md md:btn-sm ${rightButtonClass} text-left whitespace-normal break-words !h-auto !leading-[1rem]`"
            :aria-label="rightButtonLabel?.value"
          >
            {{ rightButtonLabel?.value }}
          </div>
        </Link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { RichText, Image, Link } from '@sitecore-jss/sitecore-jss-vue';
import { computed } from 'vue';
import { IPromoCardBasic } from '@/interfaces/entities/promo-card-basic.interface';
import { ButtonStyle } from '@/enums/button-style.enum';
import { mapFieldToEnum } from '@/mappers/sc-field-to-enum.mapper';
import RichTextWithModal from '@/components/RichTextWithModal/RichTextWithModal.vue';

// ----------------------------------------------------------------------------------
// Define variables
// ----------------------------------------------------------------------------------
const props = defineProps<Partial<IPromoCardBasic>>();

const hasIcon = computed(() => !!props.icon?.value?.src);
const leftButtonStyle = computed<ButtonStyle | undefined>(() => mapFieldToEnum(ButtonStyle, props.leftButtonStyle));
const rightButtonStyle = computed<ButtonStyle | undefined>(() => mapFieldToEnum(ButtonStyle, props.rightButtonStyle));
const includeBankContent = computed(() => props.includeBankContent?.value);
const leftButtonClass = computed(() => [getButtonStyleClass(leftButtonStyle.value)]);
const rightButtonClass = computed(() => [getButtonStyleClass(rightButtonStyle.value)]);

const getButtonStyleClass = (buttonStyle: ButtonStyle | undefined) => {
  switch (buttonStyle) {
    case ButtonStyle.PRIMARY:
      return 'btn-primary';
    case ButtonStyle.SECONDARY:
      return 'btn-secondary';
    case ButtonStyle.TERTIARY:
      return 'btn-tertiary';
    default:
      return '';
  }
};
</script>
